import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import OrgStructure from "./OrgStructure";
import AttendanceLog, { generateDummyAttendance } from "./Attendancelog";
import TaskKanbanBoard from "./TaskKanbanBoard";
/*
  Razorpay-Inspired Light/Dark Theme Employee Management Manifest
  ---------------------------------------------------------------
  * Complete Workforce & Department Directory with Internal Linking.
  * Easy-to-understand sidebar sub-menu names.
  * Fully switchable Light/Dark theme and real-time notification system.
  * Departments are now fetched dynamically from the API.
*/

const FONT_IMPORT_URL =
  "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap";
const ICON_IMPORT_URL = "https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/tabler-icons.min.css";



export default function UserManagementDashboard() {
  const [theme, setTheme] = useState("light");
  const [contentReady, setContentReady] = useState(false);
  const [activeTab, setActiveTab] = useState("All Departments"); // Department or feature view selected in sidebar
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("All Roles");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;
  const [employeesList, setEmployeesList] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [employeesError, setEmployeesError] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  
  // Expanded Employee Detail Drawer
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [attendanceEmpId, setAttendanceEmpId] = useState("");
  const [editingDeptForId, setEditingDeptForId] = useState(null);
  const [isSavingDept, setIsSavingDept] = useState(false);
  const [isDrawerClosing, setIsDrawerClosing] = useState(false);

  const closeDrawer = () => {
    setIsDrawerClosing(true);
    setTimeout(() => {
      setSelectedEmployee(null);
      setIsDrawerClosing(false);
    }, 200);
  };

  // Dynamic departments fetched from API
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(true);
  const [departmentsError, setDepartmentsError] = useState(null);
  // Dynamic managers (system users) fetched from API
  const [managerUsers, setManagerUsers] = useState([]);
  const [managerUsersLoading, setManagerUsersLoading] = useState(true);

  // Notification system state
  const [notifications, setNotifications] = useState([]);

  // Add Manager modal state
  const [isAddManagerModalOpen, setIsAddManagerModalOpen] = useState(false);
  const [isSavingManager, setIsSavingManager] = useState(false);
  const [newManager, setNewManager] = useState({
    departmentId: "",
    managerId: "",
  });

  // Form state for adding new employee
  const [newEmp, setNewEmp] = useState({
    name: "",
    email: "",
    id: "",
    department: "IT",
    role: "Software Engineer Intern",
    phone: "",
    status: "active",
    location: "Bangalore HQ",
    emergencyContact: "",
    supervisor: "Team Lead",
    attendance: "100% (On-time)",
    okrs: "Initial Onboarding Goals",
    assets: "Standard Laptop Bundle",
    skills: ["General Operations"],
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, [theme]);

  // Fetch departments dynamically from the API (also re-used to refresh after adding a manager)
  const fetchDepartments = async () => {
    setDepartmentsLoading(true);
    setDepartmentsError(null);
    try {
      const apiUrl = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem("token");

      const response = await fetch(
        `${apiUrl}/api/departments?limit=100&offset=0`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const data = await response.json();
      const rows = data?.entities?.rows || [];

      setDepartments(rows);
    } catch (err) {
      setDepartmentsError(err.message || "Failed to load departments");
      addNotification("Failed to load departments from server.", "error");
    } finally {
      setDepartmentsLoading(false);
    }
  };

  useEffect(() => {
    addNotification("System data synced successfully.", "success");
    fetchDepartments();
    fetchManagerUsers();
    fetchEmployees();

    const readyTimer = setTimeout(() => setContentReady(true), 500);
    return () => clearTimeout(readyTimer);
  }, []);

  // Map a raw /api/users row + department lookup into the shape the table/drawer expect
  const mapUserToEmployee = (u, departmentsById) => {
    const dept = departmentsById[u.departmentId];
    const status = u.isArchived ? "blocked" : "active";
    return {
      id: String(u.id),
      name: u.name || "Unnamed",
      email: u.email || "—",
      department: u.departmentDetails?.value || dept?.value || "N/A",
      role: u.designation || deriveRole(u),
      cityId: u.cityId ?? null,
      hubId: u.hubId ?? null,
      loggedIn: !!u.loggedIn,
      isVerified: !!u.isVerified,
      isDocVerified: !!u.isDocVerified,
      image: u.image || null,
      phone: u.mobile || "—",
      status,
      joinDate: u.createdAt ? new Date(u.createdAt).toLocaleDateString("en-GB") : "—",
      location: u.permAddr || "Bangalore HQ",
      emergencyContact: "—",
      supervisor: dept?.managerDetails?.name || "—",
      attendance: "—",
      okrs: "—",
      assets: "—",
      skills: [],
      isArchived: !!u.isArchived,
    };
  };
    const deriveRole = (u) => {
    if (u.isFranchiseAdmin) return "Franchise Admin";
    if (u.isFranchiseUser) return "Franchise User";
    if (u.isSalesAgent) return "Sales Agent";
    if (u.isAgent) return "Agent";
    return "System User";
  };

  const fetchEmployees = async () => {
    setEmployeesLoading(true);
    setEmployeesError(null);
    try {
      const apiUrl = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem("token");

      const [usersRes, deptRes] = await Promise.all([
        fetch(`${apiUrl}/api/users?limit=1000&isSystemUser=1`, {
          headers: { Authorization: `${token}` },
        }),
        fetch(`${apiUrl}/api/departments?limit=100&offset=0`, {
          headers: { Authorization: `${token}` },
        }),
      ]);

      if (!usersRes.ok) throw new Error(`Request failed with status ${usersRes.status}`);
      const usersData = await usersRes.json();
      const deptData = deptRes.ok ? await deptRes.json() : null;

      const deptRows = deptData?.entities?.rows || [];
      const departmentsById = {};
      deptRows.forEach((d) => { departmentsById[d.id] = d; });

      const rows = usersData?.entities?.rows || [];
      const mapped = rows.map((u) => mapUserToEmployee(u, departmentsById));
      setEmployeesList(mapped);
    } catch (err) {
      setEmployeesError(err.message || "Failed to load employees");
      addNotification("Failed to load employees from server.", "error");
    } finally {
      setEmployeesLoading(false);
    }
  };

  // Fetch system users eligible to be managers
  const fetchManagerUsers = async () => {
    setManagerUsersLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem("token");

      const response = await fetch(
        `${apiUrl}/api/users?limit=1000&isSystemUser=1`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const data = await response.json();
      const rows = data?.entities?.rows || [];
      setManagerUsers(rows.filter((u) => !u.isArchived));
    } catch (err) {
      addNotification(err.message || "Failed to load managers.", "error");
    } finally {
      setManagerUsersLoading(false);
    }
  };
    const handleToggleBlockUser = async (emp) => {
    const nextArchived = emp.status !== "blocked"; // toggling to blocked = isArchived: true
    try {
      const apiUrl = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem("token");

      const response = await fetch(`${apiUrl}/api/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `${token}`,
        },
        body: JSON.stringify({
          id: emp.id,
          isArchived: nextArchived,
        }),
      });

      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

      setEmployeesList((prev) =>
        prev.map((e) =>
          e.id === emp.id ? { ...e, status: nextArchived ? "blocked" : "active", isArchived: nextArchived } : e
        )
      );
      setSelectedEmployee((prev) =>
        prev && prev.id === emp.id ? { ...prev, status: nextArchived ? "blocked" : "active" } : prev
      );
      addNotification(
        `${emp.name} ${nextArchived ? "blocked" : "unblocked"} successfully.`,
        "success"
      );
    } catch (err) {
      addNotification(err.message || "Failed to update user status.", "error");
    }
  };
   const handleUpdateDepartment = async (empId, newDeptId) => {
    const dept = departments.find((d) => String(d.id) === String(newDeptId));
    if(!dept) return addNotification("Selected department not found.", "error");
    const empName = employeesList.find((e) => e.id === empId)?.name || `Employee #${empId}`;
    addNotification(`${empName} moved to ${dept.value}...`, "success");
    setIsSavingDept(true);
    try {
      const apiUrl = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem("token");

      const response = await fetch(`${apiUrl}/api/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `${token}`,
        },
        body: JSON.stringify({
          id: empId,
          departmentId: Number(dept.id),
        }),
      });

      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

      setEmployeesList((prev) =>
        prev.map((e) =>
          e.id === empId ? { ...e, department: dept.value, departmentId: dept.id } : e
        )
      );
      setSelectedEmployee((prev) =>
        prev && prev.id === empId ? { ...prev, department: dept.value, departmentId: dept.id } : prev
      );
      addNotification(`Employee #${empId} moved to ${dept.value}.`, "success");
      setEditingDeptForId(null);

      // Refetch to confirm the backend actually persisted departmentId/department
      fetchEmployees();
    } catch (err) {
      addNotification(err.message || "Failed to update department.", "error");
    } finally {
      setIsSavingDept(false);
    }
  };

    const handleExportCsv = () => {
    const headers = ["ID", "Name", "Email", "Phone", "Department", "Role", "Status", "Join Date"];
    const rows = filteredEmployees.map((e) => [
      e.id, e.name, e.email, e.phone, e.department, e.role, e.status, e.joinDate,
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `employees_${activeTab.replace(/\s+/g, "_").toLowerCase()}_${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    addNotification(`Exported ${filteredEmployees.length} employee records.`, "success");
  };

  // POST a manager assignment to the same /api/departments endpoint
  const handleAddManagerSubmit = async (e) => {
    e.preventDefault();
    if (!newManager.departmentId || !newManager.managerId) return;

    const chosenUser = managerUsers.find(
      (u) => String(u.id) === String(newManager.managerId)
    );

    setIsSavingManager(true);
    try {
      const apiUrl = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem("token");

      const response = await fetch(`${apiUrl}/api/departments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `${token}`,
        },
        body: JSON.stringify({
          id: newManager.departmentId,
          managerId: Number(newManager.managerId),
        }),
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      addNotification(
        `Manager "${chosenUser?.name || newManager.managerId}" assigned successfully.`,
        "success"
      );
      setIsAddManagerModalOpen(false);
      setNewManager({ departmentId: "", managerId: "" });

      // Refresh department list so the new manager shows up immediately
      fetchDepartments();
    } catch (err) {
      addNotification(err.message || "Failed to assign manager.", "error");
    } finally {
      setIsSavingManager(false);
    }
  };

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  const addNotification = (text, type = "success") => {
    const id = Date.now();
    const newNotif = { id, text, type, time: "Just now" };
    setNotifications((prev) => [newNotif, ...prev.slice(0, 4)]);

    setTimeout(() => {
      removeNotification(id);
    }, 5000);
  };

  const [leavingNotifIds, setLeavingNotifIds] = useState([]);

  const removeNotification = (id) => {
    setLeavingNotifIds((prev) => [...prev, id]);
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      setLeavingNotifIds((prev) => prev.filter((lid) => lid !== id));
    }, 200);
  };

  // Theme Design Tokens
  const tokens = {
    light: {
      bgCanvas: "#F8FAFC",
      bgSurface: "#FFFFFF",
      bgSubtle: "#F1F5F9",
      border: "#E2E8F0",
      textHi: "#0F172A",
      textMid: "#475569",
      textLow: "#94A3B8",
      primary: "#2B6CB0",
      accent: "#3182CE",
      badgeActiveBg: "#DCFCE7",
      badgeActiveText: "#166534",
      shadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)",
    },
    dark: {
      bgCanvas: "#0B0F19",
      bgSurface: "#111827",
      bgSubtle: "#1F2937",
      border: "#374151",
      textHi: "#F9FAFB",
      textMid: "#9CA3AF",
      textLow: "#6B7280",
      primary: "#3B82F6",
      accent: "#60A5FA",
      badgeActiveBg: "#064E3B",
      badgeActiveText: "#6EE7B7",
      shadow: "0 10px 15px -3px rgba(0, 0, 0, 0.5)",
    },
  }[theme];

  // Department names derived from the fetched API rows (dynamic, replaces hardcoded list)
  const departmentNames = useMemo(
    () => departments.map((d) => d.value),
    [departments]
  );

  // Department -> manager name lookup, used to show a Manager column against employees
  const departmentManagerMap = useMemo(() => {
    const map = {};
    departments.forEach((d) => {
      map[d.value] = d.managerDetails?.name || "—";
    });
    return map;
  }, [departments]);

  // Sidebar Sub-Menu with Easy-to-Understand Names
  const sidebarSubMenu = [
    { category: "Daily Operations", items: ["Task Board", "Attendance Logs", "Team Goals", "Equipment Tracker", "Work Shifts"] },
    { category: "Security & Control", items: ["Document Vault", "Org Structure", "Security Access", "Skill Matrix"] },
    { category: "Quick Actions", items: ["Activity Logs", "Broadcast Alerts"] }
  ];

  // Filter employees based on active sidebar tab & search query
  const roleOptions = useMemo(() => {
    const roles = new Set(employeesList.map((e) => e.role));
    return ["All Roles", ...Array.from(roles).sort()];
  }, [employeesList]);

  const filteredEmployees = useMemo(() => {
    return employeesList.filter((emp) => {
      const isDeptMatch = [...departmentNames, "All Departments"].includes(activeTab);
      const matchesDept = !isDeptMatch || activeTab === "All Departments" || emp.department === activeTab;
      const matchesRole = roleFilter === "All Roles" || emp.role === roleFilter;
      const matchesSearch =
        emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.role.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesDept && matchesRole && matchesSearch;
    });
  }, [employeesList, activeTab, roleFilter, searchTerm, departmentNames]);

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, roleFilter, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredEmployees.length / PAGE_SIZE));

  const paginatedEmployees = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredEmployees.slice(start, start + PAGE_SIZE);
  }, [filteredEmployees, currentPage]);

  const attendanceCache = React.useRef({});
  const getAttendanceFor = (empId) => {
    if (!attendanceCache.current[empId]) {
      attendanceCache.current[empId] = generateDummyAttendance({ days: 365, absentRate: 0.08 });
    }
    return attendanceCache.current[empId];
  };

  const stats = useMemo(() => {
    const total = employeesList.length;
    const active = employeesList.filter((e) => e.status === "active").length;
    const itCount = employeesList.filter((e) => e.department === "IT").length;
    const hrCount = employeesList.filter((e) => e.department === "HR").length;
    return { total, active, itCount, hrCount };
  }, [employeesList]);

  const handleAddEmployeeSubmit = (e) => {
    e.preventDefault();
    if (!newEmp.name || !newEmp.email || !newEmp.id) return;
    
    const createdEmp = {
      ...newEmp,
      joinDate: new Date().toLocaleDateString("en-GB"),
      location: "Bangalore HQ",
      emergencyContact: "+91 90000 11111",
      supervisor: "Department Head",
      attendance: "100% (On-time)",
      okrs: "Initial Onboarding Objectives",
      assets: "Standard Laptop Bundle",
      skills: ["General Operations"],
    };

    setEmployeesList([createdEmp, ...employeesList]);
    setIsAddModalOpen(false);
    addNotification(`Successfully added employee: ${newEmp.name} (ID: ${newEmp.id})`, "success");
    
    setNewEmp({
      name: "",
      email: "",
      id: "",
      department: "IT",
      role: "Software Engineer Intern",
      phone: "",
      status: "active",
      location: "Bangalore HQ",
      emergencyContact: "",
      supervisor: "Team Lead",
    });
  };

  return (
    <div
      style={{
        backgroundColor: tokens.bgCanvas,
        color: tokens.textHi,
        fontFamily: "'Inter', sans-serif",
        minHeight: "100vh",
        transition: "background-color 0.3s ease, color 0.3s ease",
      }}
      className="w-full py-8 px-4 sm:px-8 relative"
    >
      <style>{`
        @import url('${FONT_IMPORT_URL}');
        @import url('${ICON_IMPORT_URL}');
        .mono { font-family: 'JetBrains Mono', monospace; }
        ::selection { background: #3B82F6; color: white; }
        .rz-input::placeholder { color: ${tokens.textLow}; }
        .transition-all-custom { transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1); }
      `}</style>

      {/* Floating Notification System */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2.5 max-w-sm w-full pointer-events-none">
        {notifications.map((notif) => (
          <div
            key={notif.id}
            className={`pointer-events-auto flex items-center justify-between p-4 rounded-xl shadow-lg border transition-all-custom ${
              leavingNotifIds.includes(notif.id)
                ? "animate-out fade-out slide-out-to-right-3 duration-200"
                : "animate-in fade-in slide-in-from-bottom-3"
            }`}
            style={{
              backgroundColor: tokens.bgSurface,
              borderColor: tokens.border,
              boxShadow: tokens.shadow,
            }}
          >
            <div className="flex items-center gap-3">
              <div 
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: notif.type === "success" ? "#10B981" : "#EF4444" }}
              />
              <div>
                <p className="text-xs font-medium" style={{ color: tokens.textHi }}>{notif.text}</p>
                <span className="text-[10px] mono" style={{ color: tokens.textLow }}>{notif.time}</span>
              </div>
            </div>
            <button
              onClick={() => removeNotification(notif.id)}
              className="text-xs ml-3 opacity-60 hover:opacity-100 transition-opacity cursor-pointer"
              style={{ color: tokens.textMid }}
            >
              <i className="ti ti-x" />
            </button>
          </div>
        ))}
      </div>

      {/* Main Container Wrapper (95% Viewport constraint) */}
      <div className="max-w-[1440px] w-[95%] mx-auto">
        
        {/* Header Bar */}
        <div 
          className="flex flex-col sm:flex-row items-start sm:items-center justify-between pb-6 border-b gap-4 transition-all-custom"
          style={{ borderColor: tokens.border }}
        >
          <div>
            <div className="flex items-center gap-2">
              <span 
                className="text-xs font-semibold px-2.5 py-0.5 rounded-full uppercase tracking-wider mono"
                style={{ background: tokens.bgSubtle, color: tokens.primary }}
              >
                Human Resources Portal
              </span>
              <span className="text-xs" style={{ color: tokens.textLow }}>• Enterprise Workforce Registry</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mt-1.5" style={{ color: tokens.textHi }}>
              User Management Directory
            </h1>
            <p className="text-sm mt-1" style={{ color: tokens.textMid }}>
              Access team lists, department filters, and employee details easily using the sidebar sub-menu.
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Theme Toggle Button */}
            <button
              onClick={toggleTheme}
              className="flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-medium transition-all-custom shadow-xs cursor-pointer hover:scale-[1.02]"
              style={{
                backgroundColor: tokens.bgSurface,
                border: `1px solid ${tokens.border}`,
                color: tokens.textHi,
              }}
            >
              {theme === "light" ? (
                <>
                  <i className="ti ti-moon text-sm text-blue-600" />
                  <span>Dark Mode</span>
                </>
              ) : (
                <>
                  <i className="ti ti-sun text-sm text-amber-400" />
                  <span>Light Mode</span>
                </>
              )}
            </button>

            {/* Add Manager Button */}
            <button
              onClick={() => setIsAddManagerModalOpen(true)}
              className="flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-medium transition-all-custom shadow-xs cursor-pointer hover:scale-[1.02]"
              style={{
                backgroundColor: tokens.bgSurface,
                border: `1px solid ${tokens.border}`,
                color: tokens.textHi,
              }}
            >
              <i className="ti ti-user-star text-sm" style={{ color: tokens.primary }} />
              <span>Add Manager</span>
            </button>

            {/* Add Employee Button */}
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold text-white transition-all-custom shadow-md cursor-pointer hover:opacity-95 hover:scale-[1.02]"
              style={{ backgroundColor: tokens.primary }}
            >
              <i className="ti ti-user-plus text-sm" />
              <span>Add New Employee</span>
            </button>
          </div>
        </div>

        {/* Metric Summary Cards Rail */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          {[
            { label: "Total Workforce", value: stats.total, icon: "ti-users" },
            { label: "Active Employees", value: stats.active, icon: "ti-user-check" },
            { label: "IT Specialists", value: stats.itCount, icon: "ti-device-laptop" },
            { label: "HR Specialists", value: stats.hrCount, icon: "ti-id-badge" },
          ].map((card, idx) => (
            <div
              key={idx}
              className={`p-5 rounded-xl transition-all-custom hover:translate-y-[-2px] ${contentReady ? "animate-in fade-in slide-in-from-bottom-2" : "opacity-0"}`}
              style={{
                backgroundColor: tokens.bgSurface,
                border: `1px solid ${tokens.border}`,
                boxShadow: tokens.shadow,
                animationDelay: `${idx * 80}ms`,
                animationDuration: "400ms",
                animationFillMode: "backwards",
              }}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium uppercase tracking-wider" style={{ color: tokens.textMid }}>
                  {card.label}
                </span>
                <i className={`ti ${card.icon} text-base`} style={{ color: tokens.primary }} />
              </div>
              <div className="text-2xl font-bold mono mt-3" style={{ color: tokens.textHi }}>
                {String(card.value).padStart(2, "0")}
              </div>
            </div>
          ))}
        </div>

        {/* Layout with Vertical Sidebar Sub-Menu */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
          
          {/* Vertical Sidebar Sub-Menu Panel */}
          <div 
            className="md:col-span-3 p-3 rounded-xl flex flex-col gap-4 shadow-sm"
            style={{ backgroundColor: tokens.bgSurface, border: `1px solid ${tokens.border}` }}
          >
            {sidebarSubMenu.map((group, gIdx) => (
              <div key={gIdx} className="flex flex-col gap-1">
                <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider mono" style={{ color: tokens.textLow }}>
                  {group.category}
                </div>
                {group.category === "Team Directories" && departmentsLoading && (
                  <div className="px-3.5 py-2.5 text-[11px]" style={{ color: tokens.textLow }}>
                    Loading departments...
                  </div>
                )}
                {group.category === "Team Directories" && !departmentsLoading && departmentsError && (
                  <div className="px-3.5 py-2.5 text-[11px]" style={{ color: "#EF4444" }}>
                    Could not load departments
                  </div>
                )}
                {group.items.map((item) => {
                  const isActive = activeTab === item;
                  return (
                    <button
                      key={item}
                      onClick={() => setActiveTab(item)}
                      className="flex items-center justify-between px-3.5 py-2.5 rounded-lg text-xs font-semibold cursor-pointer"
                      style={{
                        backgroundColor: isActive ? tokens.primary : "transparent",
                        color: isActive ? "#FFFFFF" : tokens.textMid,
                        transform: isActive ? "scale(1.02)" : "scale(1)",
                        transition: "background-color 220ms ease, color 220ms ease, transform 220ms ease",
                      }}
                    >
                      <span>{item}</span>
                      <i className={`ti ti-chevron-right text-[10px] transition-transform ${isActive ? "translate-x-0.5" : "opacity-40"}`} />
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Main Content Area (Dynamic based on Sub-Menu Selection) */}
          <div className="md:col-span-9 flex flex-col gap-4">
            
            {/* Conditional Rendering: Employee Directory or Feature Modules */}
            {activeTab === "Task Board" ? (
  <TaskKanbanBoard />
) :activeTab === "Org Structure" ? (
              <OrgStructure theme={theme} />
            ) : activeTab === "Attendance Logs" ? (
              <div
                className="p-6 rounded-xl shadow-sm transition-all-custom"
                style={{ backgroundColor: tokens.bgSurface, border: `1px solid ${tokens.border}` }}
              >
                <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
                  <div>
                    <h3 className="text-lg font-bold" style={{ color: tokens.textHi }}>
                      Attendance Overview
                    </h3>
                    <p className="text-xs mt-0.5" style={{ color: tokens.textMid }}>
                      Select an employee to view their attendance record.
                    </p>
                  </div>
                  <select
                    value={attendanceEmpId}
                    onChange={(e) => setAttendanceEmpId(e.target.value)}
                    className="h-10 px-3 text-xs rounded-lg focus:outline-none cursor-pointer font-medium min-w-[220px]"
                    style={{ backgroundColor: tokens.bgSubtle, border: `1px solid ${tokens.border}`, color: tokens.textHi }}
                  >
                    <option value="">Select an employee...</option>
                    {employeesList.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name} (#{emp.id})
                      </option>
                    ))}
                  </select>
                </div>

                {attendanceEmpId ? (
                  <AttendanceLog
                    data={getAttendanceFor(attendanceEmpId)}
                    weeks={52}
                    theme={theme}
                  />
                ) : (
                  <div
                    className="flex flex-col items-center justify-center text-center py-16 rounded-xl"
                    style={{ backgroundColor: tokens.bgSubtle, border: `1px dashed ${tokens.border}` }}
                  >
                    <i className="ti ti-calendar-search text-2xl mb-2" style={{ color: tokens.textLow }} />
                    <p className="text-xs" style={{ color: tokens.textMid }}>
                      No employee selected. Choose one above to load their attendance.
                    </p>
                  </div>
                )}
              </div>
            ) : [...departmentNames, "All Departments"].includes(activeTab) ? (
              <>
                {/* Search & Filter Bar */}
                <div 
                  className="p-4 rounded-xl flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 transition-all-custom"
                  style={{ backgroundColor: tokens.bgSurface, border: `1px solid ${tokens.border}` }}
                >
                  <div className="relative flex-1 min-w-[220px]">
                    <i 
                      className="ti ti-search absolute left-3.5 top-1/2 -translate-y-1/2 text-sm"
                      style={{ color: tokens.textLow }}
                    />
                    <input
                      type="text"
                      placeholder="Search by name, ID (e.g. 358), or role..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="rz-input w-full h-10 pl-10 pr-4 text-xs rounded-lg focus:outline-none transition-colors"
                      style={{
                        backgroundColor: tokens.bgSubtle,
                        border: `2px solid #000000`,
                        color: tokens.textHi,
                      }}
                    />
                  </div>

                 <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    className="h-10 px-3 text-xs rounded-lg focus:outline-none cursor-pointer font-semibold text-white transition-all-custom shadow-md hover:opacity-95"
                    style={{ backgroundColor: tokens.primary, border: "none" }}
                  >
                    {roleOptions.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>

                 <button
                    onClick={handleExportCsv}
                    className="flex items-center gap-2 h-10 px-3.5 rounded-lg text-xs font-semibold text-white transition-all-custom cursor-pointer shadow-md hover:opacity-95 hover:scale-[1.02]"
                    style={{ backgroundColor: tokens.primary }}
                  >
                    <i className="ti ti-download" />
                    <span>Export CSV</span>
                  </button>

                 <select
                    value={activeTab}
                    onChange={(e) => setActiveTab(e.target.value)}
                    className="h-10 px-3 text-xs rounded-lg focus:outline-none cursor-pointer font-semibold text-white transition-all-custom shadow-md hover:opacity-95"
                    style={{ backgroundColor: tokens.primary, border: "none" }}
                  >
                    <option value="All Departments">All Departments</option>
                    {departmentNames.map((dep) => (
                      <option key={dep} value={dep}>{dep}</option>
                    ))}
                  </select>

                  <div className="text-xs mono text-right whitespace-nowrap" style={{ color: tokens.textLow }}>
                    Active View: <span style={{ color: tokens.textHi, fontWeight: 600 }}>{activeTab}</span> ({filteredEmployees.length})
                  </div>
                </div>

                {/* Employee Data Table */}
                <div 
                  className="rounded-xl overflow-hidden shadow-sm transition-all-custom"
                  style={{ backgroundColor: tokens.bgSurface, border: `1px solid ${tokens.border}` }}
                >
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr 
                          className="text-xs uppercase tracking-wider"
                          style={{ 
                            backgroundColor: tokens.bgSubtle, 
                            color: tokens.textMid,
                            borderBottom: `1px solid ${tokens.border}` 
                          }}
                        >
                          <th className="py-3.5 px-6 font-semibold mono">ID</th>
                          <th className="py-3.5 px-4 font-semibold">Employee Name & Contact</th>
                          <th className="py-3.5 px-4 font-semibold">Department</th>
                          <th className="py-3.5 px-4 font-semibold">Manager</th>
                          <th className="py-3.5 px-4 font-semibold">Role Designation</th>
                          <th className="py-3.5 px-4 font-semibold">Status</th>
                          <th className="py-3.5 px-6 font-semibold text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="text-xs divide-y" style={{ borderColor: tokens.border }}>
                        {employeesLoading ? (
                          Array.from({ length: 5 }).map((_, i) => (
                            <tr key={`skeleton-${i}`}>
                              {Array.from({ length: 7 }).map((__, j) => (
                                <td key={j} className="py-4 px-4">
                                  <div
                                    className="h-3 rounded animate-pulse"
                                    style={{ backgroundColor: tokens.bgSubtle, width: j === 1 ? "80%" : "50%" }}
                                  />
                                </td>
                              ))}
                            </tr>
                          ))
                        ) : paginatedEmployees.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="py-12 text-center" style={{ color: tokens.textMid }}>
                              No employee profiles found under <span className="font-semibold">{activeTab}</span>.
                            </td>
                          </tr>
                        ) : (
                          paginatedEmployees.map((emp, idx) => (
                            <tr 
                              key={emp.id} 
                              className={`transition-colors hover:opacity-90 group cursor-pointer ${contentReady ? "animate-in fade-in slide-in-from-bottom-1" : "opacity-0"}`}
                              style={{ backgroundColor: tokens.bgSurface, animationDelay: `${idx * 30}ms`, animationDuration: "300ms", animationFillMode: "backwards" }}
                              onClick={() => setSelectedEmployee(emp)}
                            >
                              <td className="py-4 px-6 mono font-semibold" style={{ color: tokens.primary }}>
                                #{emp.id}
                              </td>
                              <td className="py-4 px-4">
                                <div className="font-semibold text-sm hover:underline" style={{ color: tokens.textHi }}>
                                  {emp.name}
                                </div>
                                <div className="mono text-[11px] mt-0.5" style={{ color: tokens.textLow }}>
                                  {emp.email} • {emp.phone}
                                </div>
                              </td>
                              <td className="py-4 px-4" onClick={(e) => e.stopPropagation()}>
                                {editingDeptForId === emp.id ? (
                                  <select
                                    autoFocus
                                    disabled={isSavingDept}
                                    defaultValue=""
                                    onChange={(e) => {
                                      if (e.target.value) handleUpdateDepartment(emp.id, e.target.value);
                                    }}
                                    onBlur={() => setEditingDeptForId(null)}
                                    className="h-8 px-2 text-[11px] rounded-md focus:outline-none cursor-pointer font-medium"
                                    style={{ backgroundColor: tokens.bgSubtle, border: `1px solid ${tokens.border}`, color: tokens.textHi }}
                                  >
                                    <option value="" disabled>Select department</option>
                                    {departments.map((d) => (
                                      <option key={d.id} value={d.id}>{d.value}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <div className="flex items-center gap-1.5 group/dept">
                                    <span 
                                      className="px-2.5 py-1 rounded-md text-[11px] font-medium"
                                      style={{ backgroundColor: tokens.bgSubtle, color: tokens.textHi }}
                                    >
                                      {emp.department}
                                    </span>
                                    <button
                                      onClick={() => setEditingDeptForId(emp.id)}
                                      className="opacity-0 group-hover/dept:opacity-100 transition-opacity cursor-pointer"
                                      style={{ color: tokens.primary }}
                                      title="Edit department"
                                    >
                                      <i className="ti ti-pencil text-xs" />
                                    </button>
                                  </div>
                                )}
                              </td>
                              <td className="py-4 px-4" style={{ color: tokens.textHi }}>
                                {departmentManagerMap[emp.department] || "—"}
                              </td>
                              <td className="py-4 px-4 font-medium" style={{ color: tokens.textHi }}>
                                {emp.role}
                              </td>
                              <td className="py-4 px-4">
                                <span
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize"
                                  style={{
                                    backgroundColor: emp.status === "blocked" ? "#FEE2E2" : tokens.badgeActiveBg,
                                    color: emp.status === "blocked" ? "#B91C1C" : tokens.badgeActiveText,
                                  }}
                                >
                                  <span
                                    className="w-1.5 h-1.5 rounded-full"
                                    style={{ backgroundColor: emp.status === "blocked" ? "#EF4444" : "#10B981" }}
                                  />
                                  {emp.status}
                                </span>
                              </td>
                              <td className="py-4 px-6 text-right" onClick={(e) => e.stopPropagation()}>
                                <button
                                  onClick={() => setSelectedEmployee(emp)}
                                  className="px-3 py-1.5 rounded-md text-xs font-medium transition-all-custom cursor-pointer hover:scale-105"
                                  style={{
                                    backgroundColor: tokens.bgSubtle,
                                    color: tokens.textHi,
                                    border: `1px solid ${tokens.border}`,
                                  }}
                                >
                                  View Details
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div
                    className="flex items-center justify-between px-6 py-3.5 border-t text-xs"
                    style={{ borderColor: tokens.border, color: tokens.textMid }}
                  >
                    <span>
                      Page {currentPage} of {totalPages} • {filteredEmployees.length} total
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1.5 rounded-md font-medium cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{ backgroundColor: tokens.bgSubtle, border: `1px solid ${tokens.border}`, color: tokens.textHi }}
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1.5 rounded-md font-medium cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{ backgroundColor: tokens.bgSubtle, border: `1px solid ${tokens.border}`, color: tokens.textHi }}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              /* Sub-Menu Feature Views */
              <div 
                className="p-8 rounded-xl shadow-sm transition-all-custom flex flex-col items-center justify-center text-center min-h-[400px]"
                style={{ backgroundColor: tokens.bgSurface, border: `1px solid ${tokens.border}` }}
              >
                <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: tokens.bgSubtle, color: tokens.primary }}>
                  <i className="ti ti-layout-dashboard text-2xl" />
                </div>
                <h3 className="text-lg font-bold" style={{ color: tokens.textHi }}>
                  Module: {activeTab}
                </h3>
                <p className="text-xs max-w-md mt-1 mb-6" style={{ color: tokens.textMid }}>
                  Manage and monitor corporate workflows associated with {activeTab.toLowerCase()} safely from this portal view.
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setActiveTab("All Departments")}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all-custom cursor-pointer"
                    style={{ backgroundColor: tokens.bgSubtle, color: tokens.textHi, border: `1px solid ${tokens.border}` }}
                  >
                    <i className="ti ti-arrow-left text-sm" />
                    <span>Back to Directory</span>
                  </button>
                  <button
                    onClick={() => addNotification(`Action completed for ${activeTab}`, "success")}
                    className="px-4 py-2 rounded-lg text-xs font-semibold text-white transition-all cursor-pointer shadow-md"
                    style={{ backgroundColor: tokens.primary }}
                  >
                    Run {activeTab} Operation
                  </button>
                </div>
              </div>
            )}

          </div>

        </div>

      </div>

      {/* Expanded Employee Detail Drawer (Internal Linking Panel) */}
      {selectedEmployee && createPortal(
        <div 
          className={`fixed inset-0 z-[9999] flex justify-end bg-black/50 backdrop-blur-xs transition-all-custom ${
            isDrawerClosing ? "animate-out fade-out duration-200" : "animate-in fade-in duration-200"
          }`}
          onClick={closeDrawer}
        >
          <div 
            className={`w-full max-w-lg h-full p-6 shadow-2xl flex flex-col justify-between overflow-y-auto transition-all-custom ${
              isDrawerClosing ? "animate-out slide-out-to-right duration-200" : "animate-in slide-in-from-right duration-300"
            }`}
            style={{ 
              backgroundColor: tokens.bgSurface, 
              borderLeft: `1px solid ${tokens.border}` 
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              {/* Drawer Header */}
              <div className="flex justify-between items-start pb-6 border-b" style={{ borderColor: tokens.border }}>
                <div>
                  <span 
                    className="text-xs font-semibold px-2 py-0.5 rounded mono uppercase"
                    style={{ backgroundColor: tokens.bgSubtle, color: tokens.primary }}
                  >
                    Employee ID: #{selectedEmployee.id}
                  </span>
                  <h2 className="text-2xl font-bold mt-2" style={{ color: tokens.textHi }}>
                    {selectedEmployee.name}
                  </h2>
                  <p className="text-xs mt-0.5 flex items-center gap-2" style={{ color: tokens.textMid }}>
                    <span>{selectedEmployee.role} • {selectedEmployee.department} Department</span>
                    {editingDeptForId === selectedEmployee.id ? (
                      <select
                        autoFocus
                        disabled={isSavingDept}
                        defaultValue=""
                        onChange={(e) => {
                          if (e.target.value) handleUpdateDepartment(selectedEmployee.id, e.target.value);
                        }}
                        onBlur={() => setEditingDeptForId(null)}
                        className="h-7 px-2 text-[11px] rounded-md focus:outline-none cursor-pointer font-medium"
                        style={{ backgroundColor: tokens.bgSubtle, border: `1px solid ${tokens.border}`, color: tokens.textHi }}
                      >
                        <option value="" disabled>Select department</option>
                        {departments.map((d) => (
                          <option key={d.id} value={d.id}>{d.value}</option>
                        ))}
                      </select>
                    ) : (
                      <button
                        onClick={() => setEditingDeptForId(selectedEmployee.id)}
                        className="cursor-pointer"
                        style={{ color: tokens.primary }}
                        title="Edit department"
                      >
                        <i className="ti ti-pencil text-xs" />
                      </button>
                    )}
                  </p>
                </div>
                <button 
                  onClick={closeDrawer}
                  className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors cursor-pointer"
                  style={{ backgroundColor: tokens.bgSubtle, color: tokens.textHi }}
                >
                  <i className="ti ti-x text-sm" />
                </button>
              </div>

              {/* Drawer Body - Personal Details & Performance Insights */}
              <div className="mt-6 space-y-6">
                <div>
                  <h4 className="text-xs uppercase tracking-wider mono font-semibold mb-3" style={{ color: tokens.textLow }}>
                    Contact & Personal Information
                  </h4>
                  <div className="grid grid-cols-2 gap-4 p-4 rounded-xl" style={{ backgroundColor: tokens.bgSubtle }}>
                    <div>
                      <span className="text-[11px]" style={{ color: tokens.textMid }}>Email Address</span>
                      <p className="text-xs font-medium mono mt-0.5" style={{ color: tokens.textHi }}>{selectedEmployee.email}</p>
                    </div>
                    <div>
                      <span className="text-[11px]" style={{ color: tokens.textMid }}>Phone Number</span>
                      <p className="text-xs font-medium mono mt-0.5" style={{ color: tokens.textHi }}>{selectedEmployee.phone}</p>
                    </div>
                    <div>
                      <span className="text-[11px]" style={{ color: tokens.textMid }}>Office Location</span>
                      <p className="text-xs font-medium mt-0.5" style={{ color: tokens.textHi }}>{selectedEmployee.location || "Bangalore HQ"}</p>
                    </div>
                    <div>
                      <span className="text-[11px]" style={{ color: tokens.textMid }}>Emergency Contact</span>
                      <p className="text-xs font-medium mono mt-0.5" style={{ color: tokens.textHi }}>{selectedEmployee.emergencyContact || "+91 90000 00000"}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-xs uppercase tracking-wider mono font-semibold mb-3" style={{ color: tokens.textLow }}>
                    Attendance Log
                  </h4>
                  <div className="p-4 rounded-xl" style={{ backgroundColor: tokens.bgSubtle }}>
                    <AttendanceLog
                      data={generateDummyAttendance({ days: 180, absentRate: 0.08 })}
                      weeks={26}
                      theme={theme}
                    />
                  </div>
                </div>

                <div>
                  <h4 className="text-xs uppercase tracking-wider mono font-semibold mb-3" style={{ color: tokens.textLow }}>
                    Workforce & Performance Insights
                  </h4>
                  <div className="grid grid-cols-2 gap-4 p-4 rounded-xl" style={{ backgroundColor: tokens.bgSubtle }}>
                    <div>
                      <span className="text-[11px]" style={{ color: tokens.textMid }}>Attendance Rate</span>
                      <p className="text-xs font-medium mt-0.5" style={{ color: tokens.textHi }}>{selectedEmployee.attendance || "98% (On-time)"}</p>
                    </div>
                    <div>
                      <span className="text-[11px]" style={{ color: tokens.textMid }}>Active Target / OKR</span>
                      <p className="text-xs font-medium mt-0.5" style={{ color: tokens.textHi }}>{selectedEmployee.okrs || "Department Goals Met"}</p>
                    </div>
                    <div>
                      <span className="text-[11px]" style={{ color: tokens.textMid }}>Assigned Equipment</span>
                      <p className="text-xs font-medium mono mt-0.5" style={{ color: tokens.textHi }}>{selectedEmployee.assets || "Standard Hardware"}</p>
                    </div>
                    <div>
                      <span className="text-[11px]" style={{ color: tokens.textMid }}>Direct Supervisor</span>
                      <p className="text-xs font-medium mt-0.5" style={{ color: tokens.textHi }}>{selectedEmployee.supervisor || "Department Head"}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Drawer Footer Actions */}
            <div className="pt-4 border-t flex justify-end gap-3 mt-6" style={{ borderColor: tokens.border }}>
              <button
                onClick={() => {
                  addNotification(`Direct ping dispatched to ${selectedEmployee.name}`, "success");
                  closeDrawer();
                }}
                className="px-4 h-10 rounded-lg text-xs font-medium transition-all-custom cursor-pointer"
                style={{ backgroundColor: tokens.bgSubtle, color: tokens.textHi, border: `1px solid ${tokens.border}` }}
              >
                Send Ping
              </button>
              <button
                onClick={() => handleToggleBlockUser(selectedEmployee)}
                className="px-4 h-10 rounded-lg text-xs font-semibold transition-all-custom cursor-pointer"
                style={{
                  backgroundColor: selectedEmployee.status === "blocked" ? "#DCFCE7" : "#FEE2E2",
                  color: selectedEmployee.status === "blocked" ? "#166534" : "#B91C1C",
                }}
              >
                {selectedEmployee.status === "blocked" ? "Unblock User" : "Block User"}
              </button>
              <button
                onClick={closeDrawer}
                className="px-5 h-10 rounded-lg text-xs font-semibold text-white transition-all-custom cursor-pointer shadow-md"
                style={{ backgroundColor: tokens.primary }}
              >
                Close Profile
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Add Employee Modal Dialog */}
      {isAddModalOpen && createPortal(
        <div 
          className="fixed inset-0 z-[999] flex items-center justify-center p-4 backdrop-blur-xs animate-in fade-in duration-200"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.6)" }}
        >
          <div 
            className="w-full max-w-lg rounded-2xl p-6 shadow-2xl transition-all-custom"
            style={{ 
              backgroundColor: tokens.bgSurface, 
              border: `1px solid ${tokens.border}` 
            }}
          >
            <div className="flex justify-between items-center mb-5">
              <div>
                <h3 className="text-lg font-bold" style={{ color: tokens.textHi }}>
                  Onboard New Employee
                </h3>
                <p className="text-xs mt-0.5" style={{ color: tokens.textMid }}>
                  Register credentials and department allocation.
                </p>
              </div>
              <button 
                onClick={() => setIsAddModalOpen(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors cursor-pointer"
                style={{ backgroundColor: tokens.bgSubtle, color: tokens.textHi }}
              >
                <i className="ti ti-x text-sm" />
              </button>
            </div>

            <form onSubmit={handleAddEmployeeSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wider mb-1.5" style={{ color: tokens.textMid }}>
                    Full Name
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Samkith Jain"
                    value={newEmp.name}
                    onChange={(e) => setNewEmp({ ...newEmp, name: e.target.value })}
                    className="rz-input w-full h-10 px-3 text-xs rounded-lg focus:outline-none"
                    style={{ backgroundColor: tokens.bgSubtle, border: `1px solid ${tokens.border}`, color: tokens.textHi }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-wider mb-1.5" style={{ color: tokens.textMid }}>
                    Employee ID
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. 358"
                    value={newEmp.id}
                    onChange={(e) => setNewEmp({ ...newEmp, id: e.target.value })}
                    className="rz-input w-full h-10 px-3 text-xs rounded-lg focus:outline-none mono"
                    style={{ backgroundColor: tokens.bgSubtle, border: `1px solid ${tokens.border}`, color: tokens.textHi }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium uppercase tracking-wider mb-1.5" style={{ color: tokens.textMid }}>
                  Company Email
                </label>
                <input
                  type="email"
                  required
                  placeholder="samkith.jain@company.com"
                  value={newEmp.email}
                  onChange={(e) => setNewEmp({ ...newEmp, email: e.target.value })}
                  className="rz-input w-full h-10 px-3 text-xs rounded-lg focus:outline-none"
                  style={{ backgroundColor: tokens.bgSubtle, border: `1px solid ${tokens.border}`, color: tokens.textHi }}
                />
              </div>

              <div>
                <label className="block text-xs font-medium uppercase tracking-wider mb-1.5" style={{ color: tokens.textMid }}>
                  Department
                </label>
                <select
                  value={newEmp.department}
                  onChange={(e) => setNewEmp({ ...newEmp, department: e.target.value })}
                  className="w-full h-10 px-3 text-xs rounded-lg focus:outline-none cursor-pointer font-medium"
                  style={{ backgroundColor: tokens.bgSubtle, border: `1px solid ${tokens.border}`, color: tokens.textHi }}
                >
                  {departmentNames.length > 0 ? (
                    departmentNames.map((dep) => (
                      <option key={dep} value={dep}>{dep}</option>
                    ))
                  ) : (
                    <option value="IT">IT</option>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium uppercase tracking-wider mb-1.5" style={{ color: tokens.textMid }}>
                  Role Designation
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Software Engineer Intern"
                  value={newEmp.role}
                  onChange={(e) => setNewEmp({ ...newEmp, role: e.target.value })}
                  className="rz-input w-full h-10 px-3 text-xs rounded-lg focus:outline-none"
                  style={{ backgroundColor: tokens.bgSubtle, border: `1px solid ${tokens.border}`, color: tokens.textHi }}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: tokens.border }}>
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-4 h-10 rounded-lg text-xs font-medium transition-all-custom cursor-pointer"
                  style={{ backgroundColor: tokens.bgSubtle, color: tokens.textHi, border: `1px solid ${tokens.border}` }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 h-10 rounded-lg text-xs font-semibold text-white transition-all-custom cursor-pointer shadow-md hover:opacity-95"
                  style={{ backgroundColor: tokens.primary }}
                >
                  Save Employee Record
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Add Manager Modal Dialog */}
      {isAddManagerModalOpen && createPortal(
        <div
          className="fixed inset-0 z-[999] flex items-center justify-center p-4 backdrop-blur-xs animate-in fade-in duration-200"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.6)" }}
        >
          <div
            className="w-full max-w-lg rounded-2xl p-6 shadow-2xl transition-all-custom"
            style={{
              backgroundColor: tokens.bgSurface,
              border: `1px solid ${tokens.border}`
            }}
          >
            <div className="flex justify-between items-center mb-5">
              <div>
                <h3 className="text-lg font-bold" style={{ color: tokens.textHi }}>
                  Assign Department Manager
                </h3>
                <p className="text-xs mt-0.5" style={{ color: tokens.textMid }}>
                  Assign or update the manager for a department.
                </p>
              </div>
              <button
                onClick={() => setIsAddManagerModalOpen(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors cursor-pointer"
                style={{ backgroundColor: tokens.bgSubtle, color: tokens.textHi }}
              >
                <i className="ti ti-x text-sm" />
              </button>
            </div>

            <form onSubmit={handleAddManagerSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider mb-1.5" style={{ color: tokens.textMid }}>
                  Department
                </label>
                <select
                  required
                  value={newManager.departmentId}
                  onChange={(e) => setNewManager({ ...newManager, departmentId: e.target.value })}
                  className="w-full h-10 px-3 text-xs rounded-lg focus:outline-none cursor-pointer font-medium"
                  style={{ backgroundColor: tokens.bgSubtle, border: `1px solid ${tokens.border}`, color: tokens.textHi }}
                >
                  <option value="" disabled>Select a department</option>
                  {departments.map((dep) => (
                    <option key={dep.id} value={dep.id}>{dep.value}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium uppercase tracking-wider mb-1.5" style={{ color: tokens.textMid }}>
                  Manager
                </label>
                <select
                  required
                  disabled={managerUsersLoading}
                  value={newManager.managerId}
                  onChange={(e) => setNewManager({ ...newManager, managerId: e.target.value })}
                  className="w-full h-10 px-3 text-xs rounded-lg focus:outline-none cursor-pointer font-medium"
                  style={{ backgroundColor: tokens.bgSubtle, border: `1px solid ${tokens.border}`, color: tokens.textHi }}
                >
                  <option value="" disabled>
                    {managerUsersLoading ? "Loading managers..." : "Select a manager"}
                  </option>
                  {managerUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.email})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: tokens.border }}>
                <button
                  type="button"
                  onClick={() => setIsAddManagerModalOpen(false)}
                  className="px-4 h-10 rounded-lg text-xs font-medium transition-all-custom cursor-pointer"
                  style={{ backgroundColor: tokens.bgSubtle, color: tokens.textHi, border: `1px solid ${tokens.border}` }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSavingManager}
                  className="px-5 h-10 rounded-lg text-xs font-semibold text-white transition-all-custom cursor-pointer shadow-md hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ backgroundColor: tokens.primary }}
                >
                  {isSavingManager ? "Saving..." : "Save Manager"}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
}